import * as rq from '@tanstack/react-query';
import { z } from 'zod';

import { UserRole } from '@/entities/user-role';

import { RoleSchema } from '../entities/role';
import { updateRole } from '../queries/__mocked_data__/roles-state';

export namespace EditRoleMutation {
  export type Input = {
    id: string;
    name: string;
    permission: UserRole;
    siteIds: string[] | null;
  };
  export const OutputSchema = z.object({
    role: RoleSchema.pick({ id: true }),
  });
  export type Output = z.infer<typeof OutputSchema>;

  function useMutationFn() {
    return async function mutationFn(input: Input): Promise<Output> {
      // Add artificial 500ms delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      const result = updateRole(input.id, {
        name: input.name,
        permission: input.permission,
        siteIds: input.siteIds,
      });

      if (!result) {
        throw new Error(`Failed to update role with id ${input.id}`);
      }

      return result;
    };
  }

  export function useMutation() {
    const queryClient = rq.useQueryClient();
    const mutationFn = useMutationFn();

    return rq.useMutation<Output, unknown, Input>({
      mutationFn,
      async onSuccess({ role }) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['user-management', 'roles'],
          }),
          queryClient.invalidateQueries({
            queryKey: ['user-management', 'role', role.id],
          }),
        ]);
      },
    });
  }
}
